
// DANVA is the umbrella name for the two Danish water-utility data
// exchange formats this parses:
//
//  - DANVAND ("van") - drinking water/supply networks: Knude, Ledning,
//    KnudeKomponent (valves, pumps, meters, ...).
//  - DANDAS ("dandas") - waste water/sewer networks: Knude, Ledning
//    (via DelLedning segments), TVInspektion (CCTV survey reports).
//
// Both are plain, well-defined XML schemas (unlike LER's own GML/WFS,
// which LERParser.js has to guess at heuristically) - so parsing here is
// direct field-by-field extraction rather than pattern matching. The
// result is still shaped exactly like LERParser's own {owners, features}
// (see LER.js's OwnerGroup.addFeature: {owner, supply, geometry, coords,
// diameter_m?, is_trace?}), so the exact same LERLayer renderer/legend/
// simple-rendered-wireframe modes apply unchanged - see index.html's own
// separate `danva` LERLayer instance.
//
// Scope of this version:
//  - DANVAND 1.1.0 and DANDAS 2.5.2/2.6.0 - the older "Group" document
//    family (KnudeGroup/LedningGroup/...), plain XKoordinat/YKoordinat/Kote
//    elements, Knudenavn-only references. Namespace-compatible with each
//    other within DANDAS per DANVA's own migration notes.
//  - DANVAND 2.1.1 and DANDAS 3.1.1 - the newer "Samling" document family
//    (KnudeSamling/LedningSamling/...), WKT geometry, guid+navn identity,
//    a single-choice FunktionStruktur block on Knude describing its
//    real-world role (Ventil/Brønd/Pumpested/...). Built directly from the
//    real DANVAND_2.1.1/DANDAS_3.1.1 XSD packages (VAN_KnudeSamling.xsd,
//    DAS_KnudeSamling.xsd, the shared kerne KnudeType/LinieType/WktType),
//    but - like TVInspektion below - without a real instance file to check
//    field names/nesting against, so treat it as needing confirmation
//    against a real export before relying on it. FunktionStruktur's own
//    15-17 mutually-exclusive role types are each individually complex
//    (their own many fields) and aren't modeled in detail - only WHICH role
//    a Knude has is captured (see readFunktionStruktur below), not that
//    role's own further attributes.
//  - DANVAND 2.0.0 is out of scope on purpose - per LE34, it will never be
//    the format actually used, so nothing here targets it even though a
//    parallel implementation of it exists elsewhere in this codebase
//    (src/digitization/danva/, a separate colleague-built module).
//  - DANDAS 3.1.1's TV-inspektion/Brøndrapport is its OWN, much larger
//    schema bundle (a separate "TV inspektion og Brøndrapport version 3"
//    package, not just a new namespace on the 2.6.0 TVInspektionGroup
//    shape) - not read or modeled in this version. TV-inspektion parsing
//    below only covers the older DANDAS 2.5.2/2.6.0 TVInspektionGroup, and
//    even that is best-effort against the XSD alone (no real TVInspektion
//    instance file was available to verify against, unlike Knude/Ledning/
//    Komponent, which were checked against real exports) - treat it as
//    needing confirmation against a real file before relying on it for
//    anything but a first look.

import {t} from "../../digitization/i18n/index.js";

const NAMESPACE_INFO = {
	"http://danva.dk/xml/schemas/van/2010/02/08": {family: "danvand", version: "1.1.0", generation: "group"},
	"http://www.danva.dk/xml/schemas/dandas/20120102": {family: "dandas", version: "2.5.2", generation: "group"},
	"http://www.danva.dk/xml/schemas/dandas/20140701": {family: "dandas", version: "2.6.0", generation: "group"},
	"urn:danva:van:1.1.2": {family: "danvand", version: "2.1.1", generation: "samling"},
	"urn:danva:das:1.1.1": {family: "dandas", version: "3.1.1", generation: "samling"},
};

// Root element -> which of the four DANVA document kinds this is - used by
// parseDanvaFile (below) to route a batch of files dropped in together
// without the user having to say up front which file is which.
const ROOT_KIND = {
	KnudeGroup: "knude", LedningGroup: "ledning", KnudeKomponentGroup: "komponent", TVInspektionGroup: "tvinspektion",
	KnudeSamling: "knude", LedningSamling: "ledning",
};

// Processing order a batch import must respect ("Knude før Ledning" - see
// this file's own top-level comment) - lower sorts first. Exported so
// index.html's multi-file loader can sort a dropped-in batch by this
// regardless of the order files were selected/dragged in.
export const DANVA_KIND_ORDER = {knude: 0, ledning: 1, komponent: 2, tvinspektion: 3};

export function detectDanvaFormat(doc){
	let root = doc.documentElement;
	let ns = root ? root.namespaceURI : null;
	let info = ns ? NAMESPACE_INFO[ns] : null;
	if(!info){
		throw new Error(t("Ukendt eller ikke-understøttet DANVA-namespace: \"{0}\". Understøttet i denne version: DANVAND 1.1.0/2.1.1, DANDAS 2.5.2/2.6.0/3.1.1 (ikke DANVAND 2.0.0).", ns || t("(intet)")));
	}
	let kind = root ? (ROOT_KIND[root.localName] || null) : null;
	return {...info, namespace: ns, rootTag: root ? root.localName : null, kind};
}

// Single entry point for a batch import that doesn't know up front which
// file is which (see index.html's multi-file DANVA loader) - "smager på"
// each document via detectDanvaFormat (namespace -> family/version/
// generation, root element -> kind) and dispatches to whichever specific
// parser applies, generation-aware where a kind exists in both document
// families (knude/ledning). Throws (rather than returning something
// half-usable) when the root element isn't one this file knows how to
// parse at all, so a caller can report "file X wasn't recognized" per file
// instead of the whole batch failing silently.
export function parseDanvaFile(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	if(!format.kind){
		throw new Error(t("Ukendt DANVA-dokumenttype: rodelement \"{0}\".", format.rootTag));
	}

	if(format.kind === "knude"){
		return format.generation === "samling" ? parseDanvaKnuderSamling(doc, knudeIndex) : parseDanvaKnuder(doc, knudeIndex);
	}
	if(format.kind === "ledning"){
		return format.generation === "samling" ? parseDanvaLedningerSamling(doc, knudeIndex) : parseDanvaLedninger(doc, knudeIndex);
	}
	if(format.kind === "komponent"){
		return parseDanvaKomponenter(doc, knudeIndex);
	}
	// kind === "tvinspektion"
	return parseDanvaTVInspektion(doc, knudeIndex);
}

// Direct-child-only helpers (not getElementsByTagName's deep-descendant
// search, which LERParser.js's own findText() deliberately uses for GML's
// heuristic matching) - DANVA's schemas nest same-named elements at
// different levels on purpose (e.g. a Knude's own XKoordinat vs. its
// nested Daeksel's own XKoordinat), so only the immediate child is ever
// the right one here.
function directChild(elem, name){
	for(let child of elem.children){
		if(child.localName === name){
			return child;
		}
	}
	return null;
}

function directChildren(elem, name){
	let result = [];
	for(let child of elem.children){
		if(child.localName === name){
			result.push(child);
		}
	}
	return result;
}

function text(elem, name){
	let child = directChild(elem, name);
	if(!child){
		return null;
	}
	let value = (child.textContent || "").trim();
	return value.length > 0 ? value : null;
}

function num(elem, name){
	let value = text(elem, name);
	return value !== null && value !== "" ? parseFloat(value) : null;
}

// Recursively turns an XML element into a plain {tag: value|value[]|object,
// "@Attr": value} structure - not used for rendering (LERLayer only reads
// owner/supply/geometry/coords/diameter_m), only kept as each feature's
// own .danva.raw so a tooltip can show the original record and a future
// round-trip export has the untouched source to fall back on.
function elementToPlainObject(elem){
	let obj = {};
	for(let attr of elem.attributes){
		obj["@" + attr.name] = attr.value;
	}
	for(let child of elem.children){
		let value = child.children.length > 0 ? elementToPlainObject(child) : (child.textContent || "").trim();
		if(obj[child.localName] !== undefined){
			if(!Array.isArray(obj[child.localName])){
				obj[child.localName] = [obj[child.localName]];
			}
			obj[child.localName].push(value);
		}else{
			obj[child.localName] = value;
		}
	}
	return obj;
}

// Hvad en Ledning/Knude/komponent hedder i infoboksen (type/subtype) og dato (YYYY-MM-DD) - samme felter som LER-features, så GIS-panelets
// info-boks og lag virker uændret.
function dateOnly(value){
	return value ? String(value).slice(0, 10) : undefined;
}

const KOMPONENT_LABELS = {
	Beholder: () => t("Beholder"), Boring: () => t("Boring"), EnkelSamling: () => t("Samling"), KombiKomp: () => t("Kombikomponent"),
	Maalerenhed: () => t("Målerenhed"), Forbrugssted: () => t("Forbrugssted"), Pumpe: () => t("Pumpe"), Reservoir: () => t("Reservoir"),
	Tapsted: () => t("Tapsted"), Ventil: () => t("Ventil"),
};

// DANVAND-rørkataloget (RoerkatalogGroup): Varenummer -> dimension/materiale. En Ledning peger på det med Varenummer.
function readRoerkatalog(root){
	let katalog = new Map();
	let group = directChild(root, "RoerkatalogGroup");
	if(!group) return katalog;
	for(let el of directChildren(group, "Roerkatalog")){
		let nr = el.getAttribute("Varenummer");
		if(!nr) continue;
		katalog.set(nr, {nominel: num(el, "NominelDim"), indvendig: num(el, "IndvendigDim"), materiale: text(el, "MaterialeBetegnelse"), trykklasse: text(el, "Trykklasse")});
	}
	return katalog;
}

// En ledning uden rørkatalog-oplysning kan have dimensionen i sin bemærkning, fx "Eksist. 225pe" -> 225 mm.
function diameterMmFromRemark(remark){
	let match = /(\d{2,4})\s*(pe|pvc|pp|pex|st|gjj|gg|bet|eternit)\b/i.exec(remark || "");
	return match ? parseInt(match[1], 10) : null;
}

function sortBySortering(elems){
	return [...elems].sort((a, b) => parseInt(a.getAttribute("Sortering") || "0", 10) - parseInt(b.getAttribute("Sortering") || "0", 10));
}

// WKT (kerne KER_WktType.xsd: WktPunktType/WktLinjeType) - the geometry
// format DANVAND 2.1.1/DANDAS 3.1.1's "Samling" documents use in place of
// the older Group formats' own separate XKoordinat/YKoordinat/Kote
// elements. Only the tagged-Z form this same file's exporter counterpart
// writes ("POINT Z (x y z)"/"LINESTRING Z (x1 y1 z1, ...)") is needed to
// round-trip, but a bare 2D coordinate still parses (z defaults to 0),
// since the schema's own WKT pattern doesn't require a Z tag at all.
function parseWktCoords(wkt){
	let match = /\(([^()]*)\)\s*$/.exec(wkt || "");
	if(!match){
		return [];
	}
	return match[1].split(",").map(part => {
		let nums = part.trim().split(/\s+/).map(Number);
		return [nums[0], nums[1], nums.length > 2 && !Number.isNaN(nums[2]) ? nums[2] : 0];
	}).filter(([x, y]) => !Number.isNaN(x) && !Number.isNaN(y));
}

// FunktionStruktur's single active child (DANVAND 2.1.1/DANDAS 3.1.1's
// "what real-world role does this node play" choice - see this file's own
// header comment on why the 15-17 possible roles aren't modeled field by
// field). Captured generically: which role (the child's own tag name,
// e.g. "Ventil"/"Broend"/"Pumpested") plus its own fields as raw data for
// a tooltip, not deeply typed.
function readFunktionStruktur(knudeEl){
	let struktur = directChild(knudeEl, "FunktionStruktur");
	if(!struktur || struktur.children.length === 0){
		return null;
	}
	let subtypeEl = struktur.children[0];
	return {subtype: subtypeEl.localName, raw: elementToPlainObject(subtypeEl)};
}

// Parses a Knuder (KnudeGroup) document into LERLayer-ready features AND
// records every named knude's real-world coordinate into `knudeIndex`
// (Knudenavn -> {x,y,z,family}) - a Ledninger or Komponenter file loaded
// afterward (parseDanvaLedninger/parseDanvaKomponenter, below) resolves its
// own by-name references against this same index, mirroring DANVA's own
// official import køreplan ("Knude før Ledning"). Pass the SAME Map object
// across every DANVA file loaded for one project so the index accumulates.
export function parseDanvaKnuder(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	let root = doc.documentElement;
	let isVand = format.family === "danvand";
	let supply = isVand ? "vand" : "afloeb";
	let ownerId = isVand ? "danva_knude_vand" : "danva_knude_afloeb";
	let ownerLabel = isVand ? t("DANVA – Knuder (vand)") : t("DANVA – Knuder (afløb)");

	let features = [];

	for(let knudeEl of directChildren(root, "Knude")){
		let name = knudeEl.getAttribute("Knudenavn");
		let x, y, z;

		if(isVand){
			x = num(knudeEl, "XKoord");
			y = num(knudeEl, "YKoord");
			z = num(knudeEl, "Kote");
		}else{
			x = num(knudeEl, "XKoordinat");
			y = num(knudeEl, "YKoordinat");
			z = num(knudeEl, "Bundkote");
			if(z === null){
				z = num(knudeEl, "Terraenkote");
			}
		}

		if(!name || x === null || y === null){
			continue;
		}
		z = z ?? 0;

		knudeIndex.set(name, {x, y, z, family: format.family});

		features.push({
			owner: ownerId,
			supply,
			geometry: "point",
			coords: [[x, y, z]],
			type: t("Knude"),
			established: dateOnly(text(knudeEl, "DatoEtableret")),
			danva: {type: "knude", name, family: format.family, version: format.version, raw: elementToPlainObject(knudeEl)},
		});
	}

	return {owners: {[ownerId]: ownerLabel}, features, format, count: features.length, skippedCount: 0};
}

// Parses a KnudeSamling document (DANVAND 2.1.1 / DANDAS 3.1.1) - the
// "Samling" generation's Knude: guid+navn attributes, WKT Geometri instead
// of separate coordinate elements, and an optional FunktionStruktur giving
// the node's real-world role. See parseDanvaKnuder above for the older
// Group-generation equivalent this mirrors (same knudeIndex contract).
export function parseDanvaKnuderSamling(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	let root = doc.documentElement;
	let isVand = format.family === "danvand";
	let supply = isVand ? "vand" : "afloeb";
	let ownerId = isVand ? "danva_knude_vand" : "danva_knude_afloeb";
	let ownerLabel = isVand ? t("DANVA – Knuder (vand)") : t("DANVA – Knuder (afløb)");

	let features = [];

	for(let knudeEl of directChildren(root, "Knude")){
		let name = knudeEl.getAttribute("navn");
		let guid = knudeEl.getAttribute("guid");
		let point = parseWktCoords(text(knudeEl, "Geometri"))[0];
		if(!name || !point){
			continue;
		}
		let [x, y, z] = point;

		knudeIndex.set(name, {x, y, z, family: format.family, guid});

		let funktion = readFunktionStruktur(knudeEl);

		features.push({
			owner: ownerId,
			supply,
			geometry: "point",
			coords: [[x, y, z]],
			type: t("Knude"),
			danva: {
				type: "knude", name, guid, subtype: funktion ? funktion.subtype : null,
				family: format.family, version: format.version,
				raw: elementToPlainObject(knudeEl),
			},
		});
	}

	return {owners: {[ownerId]: ownerLabel}, features, format, count: features.length, skippedCount: 0};
}

// Parses a LedningSamling document (DANVAND 2.1.1 / DANDAS 3.1.1). Unlike
// the older Group generation, a Samling Ledning carries its own complete
// WKT LineString geometry (see LinieType's required Geometri element) -
// start/slut knude names are still read (and cross-checked into
// knudeIndex's own naming, same "Knude før Ledning" spirit) but aren't
// needed to place the geometry itself, so a Ledning here is never skipped
// just for referencing a knude that hasn't been loaded yet.
export function parseDanvaLedningerSamling(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	let root = doc.documentElement;
	let isVand = format.family === "danvand";
	let supply = isVand ? "vand" : "afloeb";
	let ownerId = isVand ? "danva_ledning_vand" : "danva_ledning_afloeb";
	let ownerLabel = isVand ? t("DANVA – Ledninger (vand)") : t("DANVA – Ledninger (afløb)");

	let features = [];
	let skippedCount = 0;

	for(let ledningEl of directChildren(root, "Ledning")){
		let k1 = ledningEl.getAttribute("startknudenavn");
		let k2 = ledningEl.getAttribute("slutknudenavn");
		let coords = parseWktCoords(text(ledningEl, "Geometri"));

		if(!k1 || !k2 || coords.length < 2){
			skippedCount++;
			continue;
		}

		features.push({
			owner: ownerId,
			supply,
			geometry: "line",
			coords,
			type: t("Ledning"),
			danva: {
				type: "ledning", knude1: k1, knude2: k2,
				guid: ledningEl.getAttribute("guid"),
				family: format.family, version: format.version,
				raw: elementToPlainObject(ledningEl),
			},
		});
	}

	return {owners: {[ownerId]: ownerLabel}, features, format, count: features.length, skippedCount};
}

// Parses a Ledninger (LedningGroup) document. Requires `knudeIndex` to
// already contain every knude this file's ledninger reference (see
// parseDanvaKnuder) - a ledning whose endpoint knude isn't found is
// skipped (counted in the returned skippedCount) rather than guessed at,
// same principle as the DANDAS/DANVAND import køreplan's own "Knude før
// Ledning" ordering.
export function parseDanvaLedninger(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	let root = doc.documentElement;
	let isVand = format.family === "danvand";
	let supply = isVand ? "vand" : "afloeb";
	let ownerId = isVand ? "danva_ledning_vand" : "danva_ledning_afloeb";
	let ownerLabel = isVand ? t("DANVA – Ledninger (vand)") : t("DANVA – Ledninger (afløb)");

	let features = [];
	let skippedCount = 0;
	let katalog = isVand ? readRoerkatalog(root) : new Map();

	for(let ledningEl of directChildren(root, "Ledning")){
		if(isVand){
			let k1 = ledningEl.getAttribute("Knude1");
			let k2 = ledningEl.getAttribute("Knude2");
			let n1 = knudeIndex.get(k1);
			let n2 = knudeIndex.get(k2);
			if(!n1 || !n2){
				skippedCount++;
				continue;
			}

			let coords = [[n1.x, n1.y, n1.z]];
			let punktItems = directChild(ledningEl, "PunktItems");
			let punkter = punktItems ? sortBySortering(directChildren(punktItems, "Punkt")) : [];
			for(let p of punkter){
				let px = num(p, "XKoordinat"), py = num(p, "YKoordinat");
				if(px === null || py === null){ continue; }
				let pz = num(p, "Kote");
				coords.push([px, py, pz ?? (n1.z + n2.z) / 2]);
			}
			coords.push([n2.x, n2.y, n2.z]);

			// dimension: kalibreret dim., ellers rørkatalogets (udvendig, så røret tegnes i sin rigtige tykkelse), ellers et tal i bemærkningen
			let katalogRow = katalog.get(text(ledningEl, "Varenummer"));
			let diameterMm = num(ledningEl, "KalibreretDim") || (katalogRow ? (katalogRow.nominel || katalogRow.indvendig) : null) || diameterMmFromRemark(text(ledningEl, "Bemaerkning"));

			features.push({
				owner: ownerId,
				supply,
				geometry: "line",
				coords,
				diameter_m: diameterMm ? diameterMm / 1000 : undefined,
				type: t("Ledning"),
				subtype: katalogRow && katalogRow.materiale ? katalogRow.materiale : undefined,
				established: dateOnly(text(ledningEl, "DatoEtableret")),
				danva: {
					type: "ledning", knude1: k1, knude2: k2,
					family: format.family, version: format.version,
					raw: elementToPlainObject(ledningEl),
				},
			});
		}else{
			// DANDAS: the geometry-bearing unit is DelLedning, not Ledning
			// itself - a Ledning is one or more DelLedning segments end to
			// end, each with its own up-/downstream knude and bend points
			// (Knaekpunkt: X/Y only, no Z of their own - DANDAS gives a
			// segment's elevation only at its two ends, via
			// BundloebskoteOpst/Nedst, so intermediate Z here is linearly
			// interpolated by position along the segment; a real
			// undulating invert profile isn't recoverable from this
			// format, but this is enough for a visual network, same
			// approximation LER's own Z-handling already makes elsewhere).
			let delItems = directChild(ledningEl, "DelLedningItems");
			let delLedninger = delItems ? directChildren(delItems, "DelLedning") : [];

			for(let del of delLedninger){
				let k1 = del.getAttribute("OpstroemKnudenavn");
				let k2 = del.getAttribute("NedstroemKnudenavn");
				let n1 = knudeIndex.get(k1);
				let n2 = knudeIndex.get(k2);
				if(!n1 || !n2){
					skippedCount++;
					continue;
				}

				let zOpst = num(del, "BundloebskoteOpst") ?? n1.z;
				let zNedst = num(del, "BundloebskoteNedst") ?? n2.z;

				let knaekItems = directChild(del, "KnaekpunktItems");
				let knaekker = knaekItems ? sortBySortering(directChildren(knaekItems, "Knaekpunkt")) : [];

				let coords = [[n1.x, n1.y, zOpst]];
				let totalSteps = knaekker.length + 1;
				knaekker.forEach((kp, i) => {
					let px = num(kp, "XKoordinat"), py = num(kp, "YKoordinat");
					if(px === null || py === null){ return; }
					let t = (i + 1) / totalSteps;
					coords.push([px, py, zOpst + (zNedst - zOpst) * t]);
				});
				coords.push([n2.x, n2.y, zNedst]);

				let diameterMm = num(del, "DiameterIndv");

				features.push({
					owner: ownerId,
					supply,
					geometry: "line",
					coords,
					diameter_m: diameterMm ? diameterMm / 1000 : undefined,
					type: t("Ledning"),
					danva: {
						type: "ledning", knude1: k1, knude2: k2,
						family: format.family, version: format.version,
						raw: elementToPlainObject(del),
					},
				});
			}
		}
	}

	return {owners: {[ownerId]: ownerLabel}, features, format, count: features.length, skippedCount};
}

// KnudeKomponentGroup (DANVAND only - DANDAS has no equivalent concept).
// Each sub-item type (Ventil, Pumpe, ...) becomes its own point feature,
// positioned at its parent knude's own coordinate (components have no
// coordinate of their own in this format - they're always "at the node").
const KOMPONENT_SUBTYPES = ["Beholder", "Boring", "EnkelSamling", "KombiKomp", "Maalerenhed", "Forbrugssted", "Pumpe", "Reservoir", "Tapsted", "Ventil"];

export function parseDanvaKomponenter(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	if(format.family !== "danvand"){
		throw new Error(t("Komponenter understøttes kun for DANVAND i denne version."));
	}

	let root = doc.documentElement;
	let ownerId = "danva_komponent_vand";
	let ownerLabel = t("DANVA – Komponenter (vand)");

	let features = [];
	let skippedCount = 0;

	for(let kkEl of directChildren(root, "KnudeKomponent")){
		let knudenavn = kkEl.getAttribute("Knudenavn");
		let knude = knudeIndex.get(knudenavn);
		if(!knude){
			skippedCount++;
			continue;
		}

		for(let subtype of KOMPONENT_SUBTYPES){
			let itemsEl = directChild(kkEl, `${subtype}Items`);
			let items = itemsEl ? directChildren(itemsEl, subtype) : [];
			for(let item of items){
				features.push({
					owner: ownerId,
					supply: "vand",
					geometry: "point",
					coords: [[knude.x, knude.y, knude.z]],
					type: t("Komponent"),
					subtype: KOMPONENT_LABELS[subtype] ? KOMPONENT_LABELS[subtype]() : subtype,
					established: dateOnly(text(item, "DatoEtableret")),
					danva: {
						type: "komponent", subtype, knudenavn,
						family: format.family, version: format.version,
						raw: elementToPlainObject(item),
					},
				});
			}
		}
	}

	return {owners: {[ownerId]: ownerLabel}, features, format, count: features.length, skippedCount};
}

// TVInspektionGroup (DANDAS only). Best-effort - see this file's own
// header comment on why (no real example instance to verify field names/
// nesting against, unlike Knude/Ledning/Komponent). Each TVObs
// (an observation logged at a station distance along the inspected run)
// is placed by linearly interpolating between the run's own start/slut
// knude, using MaaltStationStart as a fraction of the report's own
// MaaltLaengde - an approximation (assumes a straight run between the two
// end knude, same simplification the DANDAS Ledning parser above makes for
// bend points) rather than tracing the actual as-built pipe route, which
// this report format has no way to reference directly.
export function parseDanvaTVInspektion(doc, knudeIndex){
	let format = detectDanvaFormat(doc);
	if(format.family !== "dandas"){
		throw new Error(t("TV-inspektion understøttes kun for DANDAS."));
	}

	let root = doc.documentElement;
	let ownerId = "danva_tv_afloeb";
	let ownerLabel = t("DANVA – TV-inspektion (afløb)");

	let features = [];
	let skippedCount = 0;

	for(let tvEl of directChildren(root, "TVInspektion")){
		let rapportnr = tvEl.getAttribute("Rapportnr");
		let startNr = text(tvEl, "Startpunktnr");
		let slutNr = text(tvEl, "Slutpunktnr");
		let n1 = startNr ? knudeIndex.get(startNr) : null;
		let n2 = slutNr ? knudeIndex.get(slutNr) : null;

		if(!n1 || !n2){
			skippedCount++;
			continue;
		}

		let maaltLaengde = num(tvEl, "MaaltLaengde");
		let obsItems = directChild(tvEl, "TVObsItems");
		let obsList = obsItems ? directChildren(obsItems, "TVObs") : [];

		// The run itself, so an inspected stretch is visible even before/
		// without expanding to individual observations - same idea as
		// Ledning's own line rendering, just approximated as a straight
		// segment between its two end knude (see this function's own
		// header comment on why).
		features.push({
			owner: ownerId,
			supply: "afloeb",
			geometry: "line",
			coords: [[n1.x, n1.y, n1.z], [n2.x, n2.y, n2.z]],
			type: t("TV-inspektion"),
			danva: {
				type: "tv_run", rapportnr, startNr, slutNr,
				family: format.family, version: format.version,
				raw: elementToPlainObject(tvEl),
			},
		});

		for(let obs of obsList){
			let stationStart = num(obs, "MaaltStationStart");
			if(stationStart === null || !maaltLaengde || maaltLaengde <= 0){
				continue;
			}
			let frac = Math.max(0, Math.min(1, stationStart / maaltLaengde));
			let x = n1.x + (n2.x - n1.x) * frac;
			let y = n1.y + (n2.y - n1.y) * frac;
			let z = n1.z + (n2.z - n1.z) * frac;

			features.push({
				owner: ownerId,
				supply: "afloeb",
				geometry: "point",
				coords: [[x, y, z]],
				type: t("TV-inspektion"),
				danva: {
					type: "tv_obs", rapportnr,
					family: format.family, version: format.version,
					raw: elementToPlainObject(obs),
				},
			});
		}
	}

	return {owners: {[ownerId]: ownerLabel}, features, format, count: features.length, skippedCount};
}
